/**
 * Support queries API + TanStack Query hooks (the "Need help?" button).
 * Endpoint catalog: backend `support/views.py`.
 *
 * Everyone sends, reads and replies to their own queries. Admins see every
 * query (`mine: true` narrows to their own), move the status and read the
 * counts. New queries and replies are multipart so a screenshot can ride
 * along.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { UserRole } from "./auth";
import { api } from "./client";
import { toQueryString, type ListQuery, type Paginated } from "./types";

/* ─────────────────────────────────────────── Types */

export type QueryStatus = "open" | "in_progress" | "resolved" | "closed";

export interface QueryPerson {
  id: number;
  name: string;
  role: UserRole | "";
}

export interface QueryMessage {
  id: number;
  author: QueryPerson | null;
  body: string;
  is_team: boolean;
  /** Auth-checked API path for the image, or null. */
  screenshot_url: string | null;
  created_at: string;
}

export interface SupportQuery {
  id: number;
  number: string;
  subject: string;
  status: QueryStatus;
  status_label: string;
  reporter: QueryPerson | null;
  page_url: string;
  resolved_by: QueryPerson | null;
  resolved_at: string | null;
  last_activity_at: string;
  created_at: string;
  message_count: number;
  last_message: string;
  has_screenshot: boolean;
}

export interface SupportQueryDetail extends SupportQuery {
  messages: QueryMessage[];
}

export interface SupportQueryList extends ListQuery {
  status?: QueryStatus;
  /** Admins only: just the queries they sent themselves. */
  mine?: boolean;
}

export type QueryCounts = Record<QueryStatus | "all", number>;

export interface MessageDraft {
  body: string;
  screenshot: Blob | null;
}

/* ─────────────────────────────────────────── Helpers */

const KEY = "support-queries";

function draftForm(draft: MessageDraft, extra: Record<string, string> = {}): FormData {
  const fd = new FormData();
  fd.append("body", draft.body);
  for (const [k, v] of Object.entries(extra)) fd.append(k, v);
  if (draft.screenshot) {
    const type = draft.screenshot.type || "image/png";
    fd.append("screenshot", new File([draft.screenshot], type === "image/jpeg" ? "screenshot.jpg" : "screenshot.png", { type }));
  }
  return fd;
}

/* ─────────────────────────────────────────── Read */

export function useSupportQueries(
  q: SupportQueryList = {},
  enabled = true,
): UseQueryResult<Paginated<SupportQuery>> {
  return useQuery({
    queryKey: [KEY, "list", q],
    queryFn: () => api.get<Paginated<SupportQuery>>(`/support/queries/${toQueryString(q)}`).then((r) => r.data),
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useSupportQuery(id: number | null): UseQueryResult<SupportQueryDetail> {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => api.get<SupportQueryDetail>(`/support/queries/${id}/`).then((r) => r.data),
    enabled: id !== null,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useSupportCounts(enabled: boolean): UseQueryResult<QueryCounts> {
  return useQuery({
    queryKey: [KEY, "counts"],
    queryFn: () => api.get<QueryCounts>("/support/queries/counts/").then((r) => r.data),
    enabled,
    refetchInterval: 60_000,
  });
}

/* ─────────────────────────────────────────── Write */

function useSettle() {
  const qc = useQueryClient();
  return (detail: SupportQueryDetail) => {
    qc.setQueryData([KEY, "detail", detail.id], detail);
    void qc.invalidateQueries({ queryKey: [KEY, "list"] });
    void qc.invalidateQueries({ queryKey: [KEY, "counts"] });
  };
}

export function useSendQuery(): UseMutationResult<SupportQueryDetail, Error, MessageDraft & { pageUrl: string }> {
  const settle = useSettle();
  return useMutation({
    mutationFn: ({ pageUrl, ...draft }) =>
      api
        .post<SupportQueryDetail>(
          "/support/queries/",
          draftForm(draft, { page_url: pageUrl.slice(0, 500), user_agent: navigator.userAgent.slice(0, 400) }),
        )
        .then((r) => r.data),
    onSuccess: settle,
  });
}

export function useReplyToQuery(id: number): UseMutationResult<SupportQueryDetail, Error, MessageDraft> {
  const settle = useSettle();
  return useMutation({
    mutationFn: (draft) => api.post<SupportQueryDetail>(`/support/queries/${id}/reply/`, draftForm(draft)).then((r) => r.data),
    onSuccess: settle,
  });
}

export function useSetQueryStatus(
  id: number,
): UseMutationResult<SupportQueryDetail, Error, { status: QueryStatus; note?: string }> {
  const settle = useSettle();
  return useMutation({
    mutationFn: (payload) => api.post<SupportQueryDetail>(`/support/queries/${id}/set-status/`, payload).then((r) => r.data),
    onSuccess: settle,
  });
}
